import { createContext, useState, useContext, useEffect, useCallback } from "react";
import API from "../lib/api";
import { setSlug } from "../lib/portalSlug";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // A client portal user's branded link ("apc") becomes their URL prefix; anyone else routes normally
  useEffect(() => {
    if (user && user.role === "client") setSlug(user.portal_slug || "");
    else if (user && user.role) setSlug("");
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    if (data.requires_mfa) {
      return { requires_mfa: true, email: data.email, mfa_token: data.mfa_token };
    }
    setUser(data);
    return data;
  };

  const verifyMfa = async (email, otpCode, backupCode, mfaToken) => {
    await API.post("/mfa/verify-login", { email, otp_code: otpCode || "", backup_code: backupCode || "", mfa_token: mfaToken || "" });
    // verify-login returns {verified, access_token, user:{...}} — load the full session user (same shape as /auth/login) from the new cookie
    const { data } = await API.get("/auth/me");
    setUser(data);
    return data;
  };

  const register = async (email, password, name, role) => {
    const { data } = await API.post("/auth/register", { email, password, name, role });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await API.post("/auth/logout"); } catch {}
    try { sessionStorage.removeItem("portal.focusClient"); } catch {}
    setUser(false);
  };

  // Merge a partial update into the signed-in user (e.g. saved display preferences) without a round trip
  const patchUser = (patch) => setUser((u) => (u ? { ...u, ...patch } : u));

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, register, logout, checkAuth, patchUser, formatApiError: (d) => {
      if (d == null) return "Something went wrong.";
      if (typeof d === "string") return d;
      if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
      return String(d);
    }}}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
